import { Alert, Button } from "antd";
import type { File, ShipInfoServiceFee } from "@zhihaoscm/entity";
import {
    SHIP_INFO_SERVICE_FEE_PAY_TYPE,
    SHIP_INFO_SERVICE_FEE_STATE,
    SHIP_INFO_SERVICE_FEE_TYPE
} from "@zhihaoscm/meta";
import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { formatCurrency, formatNumber } from "@zhihaoscm/utils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fileService } from "@/api/fileService";
import { shipInfoServiceFeeService } from "@/api/shipInfoServiceFeeService";
import { shipRequirementService } from "@/api/shipRequireService";
import { BaseInfo, Breadcrumb, DetailHeader, InfoCard, ProTable, type ProColumn } from "@/components/common";
import "./ship-info-service-fee.css";

const states = SHIP_INFO_SERVICE_FEE_STATE.options
    .filter(item => Number(item.value) !== SHIP_INFO_SERVICE_FEE_STATE.UNPAID.code)
    .map(item => ({ label: item.label, value: Number(item.value) }));
/** 对齐 Vue dateTimeFormat 的默认分钟级展示。 */
const formatDetailDateTime = (value?: string) => value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "--";
/** 船务信息服务费列表固定查询货主视角。 */
export function ShipInfoServiceFeeListPage() {
    const navigate = useNavigate();
    const columns = useMemo<ProColumn<ShipInfoServiceFee>[]>(
        () => [
            {
                key: "id",
                title: "编号",
                minWidth: 114,
                search: { key: "idOrCode", type: "input", placeholder: "请输入编号、船运单编号", span: 5, order: 1 },
                render: (_, row) => (
                    <button className="ship-fee-list__link" type="button" onClick={() => navigate(`/logistics/shipInfoServiceFee/detail?id=${row.id}`)}>
                        {row.id}
                    </button>
                )
            },
            {
                key: "relationCode",
                title: "关联船运单",
                minWidth: 114,
                render: (_, row) => (
                    <button
                        className="ship-fee-list__link"
                        type="button"
                        onClick={async () => {
                            await shipRequirementService.getOrderDetail({ orderId: row.relationCode });
                            navigate(`/logistics/shipOrder/detail?id=${row.relationCode}`);
                        }}
                    >
                        {row.relationCode}
                    </button>
                )
            },
            {
                key: "amount",
                title: "金额(元)",
                minWidth: 114,
                render: value => formatNumber(value, 2)
            },
            { key: "createdTime", title: "创建时间", minWidth: 140, render: value => value ? dayjs(value as string).format("YYYY-MM-DD HH:mm") : "--" },
            {
                key: "state",
                title: "状态",
                minWidth: 72,
                enum: states,
                search: { key: "state", type: "select", placeholder: "请选择状态", options: states, span: 3, order: 2 },
                render: value => SHIP_INFO_SERVICE_FEE_STATE.getIntroByCode(Number(value)) || "--"
            }
        ],
        [navigate]
    );
    return (
        <section className="section-card ship-fee-list">
            <Breadcrumb routes={[{ name: "物流中心" }, { name: "船务信息服务费" }]} />
            <div className="table-box">
                <ProTable
                    title="船务信息服务费列表"
                    rowKey="id"
                    columns={columns}
                    initialParams={{ type: SHIP_INFO_SERVICE_FEE_TYPE.OWNER.code }}
                    request={params =>
                        shipInfoServiceFeeService.pagging({
                            ...params,
                            type: SHIP_INFO_SERVICE_FEE_TYPE.OWNER.code
                        } as never)
                    }
                />
            </div>
        </section>
    );
}

/** 服务费详情展示关联运单、船舶和线下支付凭证。 */
export function ShipInfoServiceFeeDetailPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") ?? "";
    const [info, setInfo] = useState<ShipInfoServiceFee>();
    const [file, setFile] = useState<File>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const load = async () => {
        if (!id) {
            setError(true);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        try {
            const detail = await shipInfoServiceFeeService.getDetail(id);
            setInfo(detail);
            if (detail.payFileId) {
                try {
                    setFile(await fileService.findById(detail.payFileId));
                } catch {
                    setFile(undefined);
                }
            }
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, [id]);
    // 旧 Vue 详情首屏静默等待数据，不额外展示页面级加载框。
    if (loading) return null;
    if (error || !info)
        return (
            <Alert
                type="error"
                showIcon
                message={id ? "船务信息服务费详情加载失败" : "缺少服务费 ID"}
                action={id ? <Button onClick={() => void load()}>重试</Button> : undefined}
            />
        );
    const shipName = info.shipCnName ? `${info.shipCnName}/${info.shipName}` : info.shipName;
    // 接口在不同环境可能返回数字字符串，统一归一化后再读取旧版状态元数据。
    const stateMeta = SHIP_INFO_SERVICE_FEE_STATE.getMetaByCode(Number(info.state));
    return (
        <section className="detail-container ship-fee-detail">
            <DetailHeader
                title="船务信息服务费详情"
                leftInfos={[{ label: "编号", value: info.id }]}
                rightInfos={[{ label: "创建时间", value: formatDetailDateTime(info.createdTime) }]}
                state={{ text: stateMeta?.intro }}
                menu={(
                    <Breadcrumb routes={[
                        { name: "物流中心" },
                        { name: "船务信息服务费", path: "/logistics/shipInfoServiceFee" },
                        { name: "船务信息服务费详情" }
                    ]} />
                )}
            />
            <div className="ship-fee-detail__json">
                <InfoCard title="基本信息">
                    <BaseInfo
                        labelPlacement="top"
                        items={[
                            {
                                label: "关联船运单",
                                value: (
                                    <button
                                        className="ship-fee-detail__link"
                                        type="button"
                                        onClick={async () => {
                                            await shipRequirementService.getOrderDetail({ orderId: info.relationCode });
                                            navigate(`/logistics/shipOrder/detail?id=${info.relationCode}`);
                                        }}
                                    >
                                        {info.relationCode}
                                    </button>
                                )
                            },
                            {
                                label: "船舶",
                                value: (
                                    <button
                                        className="ship-fee-detail__link"
                                        type="button"
                                        onClick={() => navigate(`/logistics/findLocation?mmsi=${info.shipId}`)}
                                    >
                                        {shipName}
                                    </button>
                                )
                            }
                        ]}
                    />
                </InfoCard>
                <InfoCard title="支付信息">
                    <BaseInfo
                        labelPlacement="top"
                        items={[
                            {
                                label: "金额",
                                value: `¥${formatCurrency(info.amount)}`
                            },
                            {
                                label: "方式",
                                value: SHIP_INFO_SERVICE_FEE_PAY_TYPE.getIntroByCode(info.payType) || "--"
                            },
                            {
                                label: "凭证文件",
                                value: file?.path ? (
                                    <a href={file.path} target="_blank" rel="noreferrer">
                                        查看/下载
                                    </a>
                                ) : (
                                    "--"
                                )
                            },
                            { label: "时间", value: formatDetailDateTime(info.payTime) }
                        ]}
                    />
                </InfoCard>
            </div>
            <div className="detail-buttons">
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
        </section>
    );
}
